import React, { useEffect, useState } from "react";
import DashboardHeader from "./dashboard_header";
import SideMenu from "./dashboard_menu";
import DashboardFooter from "./dashboard_footer";
import Canvas from "./dahboard_canvas";
import axios from "axios";
import localforage from "localforage";
import { useBaseUrl } from "../../utils/utils";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";

const ACCENT = '#0f766e';
const ACCENT_SOFT = 'rgba(15, 118, 110, 0.10)';

interface Facility {
    id: number;
    name: string;
    email: string;
    phone?: string;
    town?: string;
    county?: string;
    type?: string;
    status?: string;
    logo?: string;
    category?: string;
    active?: boolean;
    staff_count?: number;
}

export const DClinics: React.FC = () => {
    const [facilities, setFacilities] = useState<Facility[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [activeClinicId, setActiveClinicId] = useState<number | null>(null);
    const base_url = useBaseUrl();

    useEffect(() => {
        document.title = "Facility Manager - Uzima";
        loadData();
    }, []);

    const loadData = async () => {
        setIsLoading(true);
        try {
            const [currentClinic, currentStaff] = await Promise.all([
                localforage.getItem("clinic_id") as Promise<number>,
                localforage.getItem("staff_id") as Promise<number>,
            ]);
            setActiveClinicId(currentClinic);

            const res = await axios.post(`${base_url}facilities/clinics/switchable`, {
                staff_id: currentStaff,
                clinic_id: currentClinic,
            }, { timeout: 10000 });
            const d = res.data;
            if (d?.status === 'success' && Array.isArray(d.data?.facilities)) {
                setFacilities(d.data.facilities.map((item: any) => ({
                    id: item.id,
                    name: item.name,
                    email: item.email,
                    phone: item.phone,
                    town: item.town,
                    type: item.category,
                    category: item.category,
                    status: item.is_current ? 'Active' : (item.active ? 'Available' : 'Inactive'),
                    active: item.active,
                    staff_count: item.staff_count,
                })));
            } else {
                setFacilities([]);
            }
        } catch (e) {
            console.error("Error loading facilities:", e);
            setFacilities([]);
        } finally {
            setIsLoading(false);
        }
    };

    const switchToFacility = async (fac: Facility) => {
        const result = await Swal.fire({
            title: 'Switch Facility?',
            text: `Switch active facility to "${fac.name}"?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: ACCENT,
            confirmButtonText: 'Switch',
        });
        if (!result.isConfirmed) return;
        try {
            await localforage.setItem("clinic_id", fac.id);
            await localforage.setItem("clinic_name", fac.name);
            if (fac.email) await localforage.setItem("clinic_email", fac.email);
            Swal.fire({ icon: 'success', title: 'Switched', text: `Now operating as "${fac.name}"`, timer: 1500, showConfirmButton: false });
            setActiveClinicId(fac.id);
        } catch (e) {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to switch facility' });
        }
    };

    const filtered = facilities.filter(f => {
        if (!search) return true;
        const t = search.toLowerCase();
        return (f.name || '').toLowerCase().includes(t) || (f.email || '').toLowerCase().includes(t) || (f.town || '').toLowerCase().includes(t) || (f.type || '').toLowerCase().includes(t);
    });

    return (
        <>
            <DashboardHeader />
            <SideMenu />
            <div className="main-content">
                <div className="page-content">
                    <div className="sc-wrap">

                        {/* Header */}
                        <div className="sc-header">
                            <div>
                                <h1><i className="bx bx-building-house me-2" style={{ color: ACCENT }}></i>Facility Manager</h1>
                                <p>View and manage your registered facilities</p>
                            </div>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <button className="sc-btn sc-btn-ghost" onClick={loadData}><i className="bx bx-refresh"></i> Refresh</button>
                                <Link to="/institution/add-clinic" className="sc-btn sc-btn-primary" style={{ textDecoration: 'none' }}><i className="bx bx-plus"></i> Add Facility</Link>
                            </div>
                        </div>

                        {/* Stats */}
                        <div className="sc-stats" style={{ marginBottom: 20 }}>
                            {[
                                { label: 'Total Facilities', val: facilities.length, icon: 'bx bx-building-house', cls: 'teal' },
                                { label: 'Active', val: facilities.filter(f => (f.status || 'active') === 'active').length, icon: 'bx bx-check-circle', cls: 'blue' },
                                { label: 'Types', val: new Set(facilities.map(f => f.type).filter(Boolean)).size || 0, icon: 'bx bx-category', cls: 'amber' },
                                { label: 'Locations', val: new Set(facilities.map(f => f.town || f.county).filter(Boolean)).size || 0, icon: 'bx bx-map', cls: 'violet' },
                            ].map((s, i) => (
                                <div className="sc-stat" key={i}>
                                    <div className={`sc-stat-icon ${s.cls}`}><i className={s.icon}></i></div>
                                    <div className="sc-stat-value">{s.val}</div>
                                    <div className="sc-stat-label">{s.label}</div>
                                </div>
                            ))}
                        </div>

                        {/* Search */}
                        <div className="sc-card" style={{ marginBottom: 16 }}>
                            <div className="sc-card-body" style={{ padding: '14px 20px' }}>
                                <div className="sc-search" style={{ marginBottom: 0 }}>
                                    <i className="bx bx-search"></i>
                                    <input type="text" placeholder="Search facilities by name, email, type, or location..." value={search} onChange={e => setSearch(e.target.value)} />
                                </div>
                            </div>
                        </div>

                        {isLoading ? (
                            <div className="sc-card">
                                <div className="sc-card-body" style={{ textAlign: 'center', padding: '60px 0' }}>
                                    <i className="bx bx-loader-alt bx-spin" style={{ fontSize: '2rem', color: ACCENT }}></i>
                                    <p style={{ marginTop: 12, color: 'var(--uzima-shell-text-muted)' }}>Loading facilities...</p>
                                </div>
                            </div>
                        ) : filtered.length === 0 ? (
                            <div className="sc-card">
                                <div className="sc-card-body" style={{ textAlign: 'center', padding: '60px 0' }}>
                                    <div style={{ width: 60, height: 60, borderRadius: '50%', background: ACCENT_SOFT, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
                                        <i className="bx bx-building-house" style={{ fontSize: '1.5rem', color: ACCENT }}></i>
                                    </div>
                                    <p style={{ fontWeight: 700 }}>{search ? 'No Matching Facilities' : 'No Facilities Registered'}</p>
                                    <p style={{ fontSize: '0.82rem', color: 'var(--uzima-shell-text-muted)' }}>
                                        {search ? 'Try a different search term.' : 'Get started by registering your first facility.'}
                                    </p>
                                    {!search && (
                                        <Link to="/institution/add-clinic" className="sc-btn sc-btn-primary" style={{ textDecoration: 'none' }}>
                                            <i className="bx bx-plus me-1"></i> Add Facility
                                        </Link>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 14 }}>
                                {filtered.map((f) => {
                                    const isActive = f.id === activeClinicId;
                                    return (
                                        <div key={f.id} className="sc-card" style={{ margin: 0, transition: 'all 0.15s', borderLeft: isActive ? `3px solid ${ACCENT}` : undefined }}
                                            onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.boxShadow = '0 4px 16px rgba(0,0,0,0.06)'; }}
                                            onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.boxShadow = ''; }}
                                        >
                                            <div className="sc-card-body" style={{ padding: '18px 20px' }}>
                                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 14 }}>
                                                    <div style={{ width: 48, height: 48, borderRadius: 12, background: isActive ? `linear-gradient(135deg, ${ACCENT}, #14b8a6)` : 'rgba(15,23,42,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                        <i className="bx bx-building-house" style={{ color: isActive ? '#fff' : '#9ca3af', fontSize: '1.2rem' }}></i>
                                                    </div>
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>{f.name}</div>
                                                        <div style={{ fontSize: '0.72rem', color: 'var(--uzima-shell-text-muted)' }}>{f.email || 'No email'}</div>
                                                    </div>
                                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                                                        <span style={{ padding: '3px 10px', borderRadius: 20, background: isActive ? ACCENT_SOFT : 'rgba(0,0,0,0.04)', color: isActive ? ACCENT : '#9ca3af', fontSize: '0.65rem', fontWeight: 700 }}>
                                                            {isActive ? 'Active' : f.status || 'Registered'}
                                                        </span>
                                                    </div>
                                                </div>

                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 14 }}>
                                                    <div style={{ fontSize: '0.74rem' }}>
                                                        <span style={{ color: 'var(--uzima-shell-text-muted)' }}>Category: </span>
                                                        <span style={{ fontWeight: 600 }}>{f.category || f.type || 'Clinic'}</span>
                                                    </div>
                                                    <div style={{ fontSize: '0.74rem' }}>
                                                        <span style={{ color: 'var(--uzima-shell-text-muted)' }}>Location: </span>
                                                        <span style={{ fontWeight: 600 }}>{f.town || f.county || '—'}</span>
                                                    </div>
                                                    <div style={{ fontSize: '0.74rem' }}>
                                                        <span style={{ color: 'var(--uzima-shell-text-muted)' }}>Team: </span>
                                                        <span style={{ fontWeight: 600 }}>{f.staff_count || 0} staff</span>
                                                    </div>
                                                    {f.phone && (
                                                        <div style={{ fontSize: '0.74rem' }}>
                                                            <span style={{ color: 'var(--uzima-shell-text-muted)' }}>Phone: </span>
                                                            <span style={{ fontWeight: 600 }}>{f.phone}</span>
                                                        </div>
                                                    )}
                                                </div>

                                                <div style={{ display: 'flex', gap: 8 }}>
                                                    {!isActive && (
                                                        <button className="sc-btn sc-btn-primary sc-btn-sm" style={{ flex: 1 }} onClick={() => switchToFacility(f)}>
                                                            <i className="bx bx-transfer-alt me-1"></i> Switch To
                                                        </button>
                                                    )}
                                                    <Link to="/institution/clinic-details" className="sc-btn sc-btn-ghost sc-btn-sm" style={{ flex: 1, textDecoration: 'none', textAlign: 'center' }}>
                                                        <i className="bx bx-show me-1"></i> Details
                                                    </Link>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                    </div>
                </div>
            </div>
            <Canvas />
            <DashboardFooter />
        </>
    );
};

export default DClinics;
